import PptxGenJS from "pptxgenjs";
import { loadCovers, addCoverSlides } from "./pptxCovers";

/**
 * Gera um PPTX executivo do MAPA ESTRATÉGICO (para a diretoria).
 * Mantém todas as informações da tela: por perspectiva -> objetivos (com
 * performance) -> indicadores (nome, % atingido com barra, meta, realizado).
 * Identidade Marini/Marply: bordô + grafite, acento amarelo.
 *
 * @param {Object} p
 * @param {Array}  p.strategicMapData  perspectivas -> objetivos -> indicadores
 * @param {string} p.selectedPeriod    ex.: "2026-05"
 * @param {Object} p.globalPerf        { performance, periodLabel, previous, trend, perspectives[] }
 */
export async function generateStrategicMapPPTX({ strategicMapData, selectedPeriod, globalPerf, covers: coversOverride }) {
  // ---- Paleta ----
  const BORDO = "7A2433";
  const GRAFITE = "2B2B30";
  const AMARELO = "E8D833";
  const CINZA_BG = "F5F6F8";
  const CINZA_LN = "E5E7EB";
  const CINZA_TX = "475569";
  const CINZA_MUT = "9CA3AF";
  const BRANCO = "FFFFFF";

  // Faixa de performance (mesma escala das barras de progresso da tela)
  const mapColor = (v) => (v >= 100 ? "059669" : v >= 90 ? "2563EB" : v >= 70 ? "D97706" : "DC2626");
  // Escala do sumário (cards globais) — consistente com o Scorecard
  const perfHex = (v) => (v >= 100 ? "059669" : v >= 80 ? "D97706" : "DC2626");

  const formatValue = (value, unit) => {
    if (typeof value === "number" && !isNaN(value)) {
      if (unit === "R$") {
        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
      }
      return `${value}${unit ? " " + unit : ""}`;
    }
    return "N/D";
  };

  const data = Array.isArray(strategicMapData) ? strategicMapData : [];
  const periodUpper = ((globalPerf && globalPerf.periodLabel) || selectedPeriod || "").toString().toUpperCase();

  const perspList =
    globalPerf && Array.isArray(globalPerf.perspectives) && globalPerf.perspectives.length
      ? globalPerf.perspectives.map((p) => ({ name: p.perspective, value: p.performance || 0 }))
      : data.map((p) => ({ name: p.perspective, value: Number(p.average) || 0 }));

  const globalValue =
    globalPerf && typeof globalPerf.performance === "number"
      ? globalPerf.performance
      : perspList.length
        ? perspList.reduce((s, p) => s + (p.value || 0), 0) / perspList.length
        : 0;

  const today = new Date().toLocaleDateString("pt-BR");

  // ---- Documento ----
  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: "MARPLY", width: 13.333, height: 7.5 });
  pptx.layout = "MARPLY";
  pptx.author = "Marini BSC";
  pptx.company = "Marini Indústria de Compensados";
  pptx.subject = "Mapa Estratégico";
  pptx.title = `Mapa Estratégico — ${periodUpper}`;

  const PW = 13.333;
  const FOOTER = "Marini · Mapa Estratégico · Confidencial — uso interno da Diretoria";

  const addFooter = (slide, pageNo) => {
    slide.addText(FOOTER, { x: 0.5, y: 7.1, w: 10.5, h: 0.3, fontFace: "Arial", fontSize: 8, color: CINZA_MUT, valign: "middle" });
    if (pageNo) slide.addText(String(pageNo), { x: 12.4, y: 7.1, w: 0.5, h: 0.3, fontFace: "Arial", fontSize: 8, color: CINZA_MUT, align: "right", valign: "middle" });
  };

  // ================= CAPA + ABERTURA (template Marini) =================
  const covers = await loadCovers("mapa", coversOverride);
  const introCount = addCoverSlides(pptx, covers, PW, 7.5);

  // Capa textual padrão (fallback caso as imagens do template não carreguem)
  if (introCount === 0) {
    const s = pptx.addSlide();
    s.background = { color: BORDO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.35, h: 7.5, fill: { color: AMARELO } });
    s.addShape(pptx.ShapeType.rect, { x: 0.35, y: 0, w: 0.12, h: 7.5, fill: { color: GRAFITE } });
    s.addText("MAPA ESTRATÉGICO", { x: 1.0, y: 2.5, w: 11.4, h: 1.1, fontFace: "Arial", fontSize: 46, bold: true, color: BRANCO });
    s.addShape(pptx.ShapeType.rect, { x: 1.05, y: 3.65, w: 3.2, h: 0.06, fill: { color: AMARELO } });
    s.addText("Marini · Indústria de Compensados", { x: 1.0, y: 3.85, w: 11.4, h: 0.5, fontFace: "Arial", fontSize: 22, color: AMARELO });
    s.addText(
      [
        { text: "Período de Referência: ", options: { color: "E7CDD3" } },
        { text: periodUpper, options: { bold: true, color: BRANCO } },
      ],
      { x: 1.0, y: 4.55, w: 11.4, h: 0.4, fontFace: "Arial", fontSize: 16 }
    );
    s.addText(`Gerado em: ${today}`, { x: 1.0, y: 4.95, w: 11.4, h: 0.4, fontFace: "Arial", fontSize: 13, color: "E7CDD3" });
    s.addText("Relatório executivo — Diretoria", { x: 1.0, y: 6.7, w: 11.4, h: 0.4, fontFace: "Arial", fontSize: 11, italic: true, color: "E7CDD3" });
  }

  // ================= SUMÁRIO EXECUTIVO =================
  {
    const s = pptx.addSlide();
    s.background = { color: BRANCO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: PW, h: 0.9, fill: { color: BORDO } });
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0.9, w: PW, h: 0.06, fill: { color: AMARELO } });
    s.addText("Sumário Executivo", { x: 0.5, y: 0, w: 8, h: 0.9, fontFace: "Arial", fontSize: 24, bold: true, color: BRANCO, valign: "middle" });
    s.addText(periodUpper, { x: PW - 4.5, y: 0, w: 4.0, h: 0.9, fontFace: "Arial", fontSize: 16, color: "F1D9DE", align: "right", valign: "middle" });

    s.addText("PERFORMANCE GLOBAL", { x: 0.5, y: 1.35, w: PW - 1, h: 0.4, fontFace: "Arial", fontSize: 16, bold: true, color: CINZA_TX, align: "center", charSpacing: 2 });
    s.addText(`${globalValue.toFixed(1)}%`, { x: 0.5, y: 1.7, w: PW - 1, h: 1.5, fontFace: "Arial", fontSize: 88, bold: true, color: perfHex(globalValue), align: "center" });
    if (globalPerf && globalPerf.previous != null) {
      const diff = globalValue - globalPerf.previous;
      const color = globalPerf.trend === "up" ? "059669" : globalPerf.trend === "down" ? "DC2626" : "6B7280";
      const arrow = globalPerf.trend === "up" ? "↗" : globalPerf.trend === "down" ? "↘" : "—";
      s.addText(`${arrow} ${diff.toFixed(1)} p.p. vs. período anterior`, { x: 0.5, y: 3.2, w: PW - 1, h: 0.4, fontFace: "Arial", fontSize: 15, bold: true, color, align: "center" });
    }

    const nCards = Math.min(perspList.length, 4);
    if (nCards > 0) {
      const gap = 0.3, cardH = 2.15, cardY = 4.0, totalW = 12.3;
      const cardW = (totalW - gap * (nCards - 1)) / nCards;
      const startX = (PW - totalW) / 2;
      perspList.slice(0, 4).forEach((p, i) => {
        const x = startX + i * (cardW + gap);
        const col = perfHex(p.value);
        s.addShape(pptx.ShapeType.roundRect, { x, y: cardY, w: cardW, h: cardH, rectRadius: 0.1, fill: { color: CINZA_BG }, line: { color: col, width: 1.75 } });
        s.addShape(pptx.ShapeType.rect, { x: x + 0.12, y: cardY + 0.22, w: cardW - 0.24, h: 0.06, fill: { color: col } });
        s.addText(p.name, { x: x + 0.15, y: cardY + 0.42, w: cardW - 0.3, h: 0.5, fontFace: "Arial", fontSize: 16, color: GRAFITE, bold: true, align: "center", valign: "middle" });
        s.addText(`${(p.value || 0).toFixed(1)}%`, { x: x + 0.15, y: cardY + 0.95, w: cardW - 0.3, h: 1.0, fontFace: "Arial", fontSize: 40, bold: true, color: col, align: "center", valign: "middle" });
      });
    }
    addFooter(s, introCount + 1);
  }

  // ================= SLIDES POR PERSPECTIVA (layout executivo) =================
  // Cada objetivo é um painel; cada indicador tem barra de progresso real.
  const perspTheme = (name) => {
    const u = (name || "").toUpperCase();
    if (u.includes("FINANC")) return { main: "059669", dark: "065F46", tint: "ECFDF5" };
    if (u.includes("CLIENTE")) return { main: "2563EB", dark: "1E3A8A", tint: "EFF6FF" };
    if (u.includes("PROCESSO")) return { main: "7C3AED", dark: "5B21B6", tint: "F5F3FF" };
    if (u.includes("PESSOA")) return { main: "EA580C", dark: "9A3412", tint: "FFF7ED" };
    return { main: BORDO, dark: "5E1A26", tint: "FBEBEE" };
  };

  const CX = 0.62, CW = 12.15;
  const BODY_TOP = 1.62, BODY_BOTTOM = 6.95;
  const OBJ_H = 0.48, IND_H = 0.58, GAP_OBJ = 0.18;
  const IND_NAME_X = CX + 0.15, IND_NAME_W = 4.5;
  const META_X = CX + 4.8, META_W = 1.95;
  const REAL_X = CX + 6.85, REAL_W = 1.95;
  const BAR_X = CX + 8.95, BAR_W = 2.2;
  const PCT_X = BAR_X + BAR_W + 0.12, PCT_W = 0.8;

  let pageNo = introCount + 2;

  const drawSlideHeader = (s, persp, theme, cont) => {
    s.background = { color: BRANCO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.22, h: 7.5, fill: { color: theme.main } });
    s.addText((persp.perspective || "").toUpperCase() + (cont ? "  (cont.)" : ""), {
      x: CX, y: 0.28, w: 8.2, h: 0.6, fontFace: "Arial", fontSize: 27, bold: true, color: theme.dark,
    });
    const avg = Number(persp.average) || 0;
    const nObj = (persp.objectives || []).length;
    s.addText(`Mapa Estratégico  ·  ${periodUpper}  ·  ${nObj} objetivo(s)`, {
      x: CX + 0.02, y: 0.9, w: 8.0, h: 0.35, fontFace: "Arial", fontSize: 12, color: CINZA_MUT,
    });
    s.addText("PERFORMANCE MÉDIA", { x: PW - 5.1, y: 0.34, w: 4.5, h: 0.3, fontFace: "Arial", fontSize: 10, bold: true, color: CINZA_TX, align: "right", charSpacing: 1 });
    s.addText(`${persp.average != null ? persp.average : 0}%`, { x: PW - 5.1, y: 0.54, w: 4.5, h: 0.72, fontFace: "Arial", fontSize: 34, bold: true, color: mapColor(avg), align: "right" });
    s.addShape(pptx.ShapeType.rect, { x: CX, y: 1.42, w: CW, h: 0.03, fill: { color: theme.main } });
  };

  const drawObjHeader = (s, y, obj, theme) => {
    const a = Number(obj.average) || 0;
    s.addShape(pptx.ShapeType.roundRect, { x: CX, y, w: CW, h: OBJ_H, rectRadius: 0.06, fill: { color: theme.tint }, line: { color: theme.main, width: 0.75 } });
    s.addShape(pptx.ShapeType.rect, { x: CX, y: y + 0.08, w: 0.08, h: OBJ_H - 0.16, fill: { color: theme.main } });
    s.addText(obj.objective || "", { x: CX + 0.24, y, w: CW - 2.0, h: OBJ_H, fontFace: "Arial", fontSize: 13, bold: true, color: "1F2937", valign: "middle" });
    const pillW = 1.45;
    s.addShape(pptx.ShapeType.roundRect, { x: CX + CW - pillW - 0.12, y: y + 0.08, w: pillW, h: OBJ_H - 0.16, rectRadius: 0.08, fill: { color: mapColor(a) } });
    s.addText(`${obj.average != null ? obj.average : 0}%`, { x: CX + CW - pillW - 0.12, y: y + 0.08, w: pillW, h: OBJ_H - 0.16, fontFace: "Arial", fontSize: 13, bold: true, color: BRANCO, align: "center", valign: "middle" });
  };

  const drawIndicator = (s, y, ind) => {
    const pct = typeof ind.percentAchieved === "number" ? ind.percentAchieved : null;
    const tier = pct != null ? mapColor(pct) : CINZA_MUT;
    s.addShape(pptx.ShapeType.rect, { x: CX + 0.15, y: y + IND_H - 0.02, w: CW - 0.3, h: 0.012, fill: { color: "EEF1F4" } });
    s.addText(ind.indicator || "", { x: IND_NAME_X, y, w: IND_NAME_W, h: IND_H, fontFace: "Arial", fontSize: 11, color: "334155", valign: "middle" });
    s.addText([{ text: "Meta  ", options: { color: CINZA_MUT, fontSize: 9 } }, { text: formatValue(ind.target, ind.unit), options: { color: "334155", fontSize: 10.5, bold: true } }], { x: META_X, y, w: META_W, h: IND_H, fontFace: "Arial", align: "left", valign: "middle" });
    s.addText([{ text: "Real  ", options: { color: CINZA_MUT, fontSize: 9 } }, { text: formatValue(ind.actual, ind.unit), options: { color: "0F172A", fontSize: 10.5, bold: true } }], { x: REAL_X, y, w: REAL_W, h: IND_H, fontFace: "Arial", align: "left", valign: "middle" });
    const barY = y + IND_H / 2 - 0.1;
    s.addShape(pptx.ShapeType.roundRect, { x: BAR_X, y: barY, w: BAR_W, h: 0.2, rectRadius: 0.1, fill: { color: "EEF1F4" } });
    if (pct != null) {
      const fw = Math.max(0.06, (BAR_W * Math.min(Math.max(pct, 0), 100)) / 100);
      s.addShape(pptx.ShapeType.roundRect, { x: BAR_X, y: barY, w: fw, h: 0.2, rectRadius: 0.1, fill: { color: tier } });
    }
    s.addText(pct != null ? `${pct.toFixed(1)}%` : "N/D", { x: PCT_X, y, w: PCT_W, h: IND_H, fontFace: "Arial", fontSize: 11, bold: true, color: tier, align: "right", valign: "middle" });
  };

  data.forEach((persp) => {
    const theme = perspTheme(persp.perspective);
    const objectives = persp.objectives || [];
    let s = pptx.addSlide();
    drawSlideHeader(s, persp, theme, false);
    addFooter(s, pageNo++);
    let y = BODY_TOP;
    if (objectives.length === 0) return;

    objectives.forEach((obj) => {
      const inds = obj.indicators || [];
      if (y + OBJ_H + IND_H > BODY_BOTTOM) {
        s = pptx.addSlide(); drawSlideHeader(s, persp, theme, true); addFooter(s, pageNo++); y = BODY_TOP;
      }
      drawObjHeader(s, y, obj, theme); y += OBJ_H + 0.08;
      if (inds.length === 0) { y += GAP_OBJ; return; }
      inds.forEach((ind) => {
        if (y + IND_H > BODY_BOTTOM) {
          s = pptx.addSlide(); drawSlideHeader(s, persp, theme, true); addFooter(s, pageNo++); y = BODY_TOP;
          drawObjHeader(s, y, obj, theme); y += OBJ_H + 0.08;
        }
        drawIndicator(s, y, ind); y += IND_H;
      });
      y += GAP_OBJ;
    });
  });

  // ================= SLIDE FINAL — LEGENDA =================
  {
    const s = pptx.addSlide();
    s.background = { color: BRANCO };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: PW, h: 0.9, fill: { color: GRAFITE } });
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0.9, w: PW, h: 0.06, fill: { color: AMARELO } });
    s.addText("Legenda & Metodologia", { x: 0.5, y: 0, w: 10, h: 0.9, fontFace: "Arial", fontSize: 22, bold: true, color: BRANCO, valign: "middle" });

    const bullets = [
      { text: "% ATINGIDO  desempenho do indicador no período (realizado vs. meta), com barra de progresso.", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "PERFORMANCE (objetivo/perspectiva)  média de desempenho dos indicadores que a compõem.", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "PERFORMANCE GLOBAL  média ponderada normalizada (pesos de indicador, objetivo e perspectiva).", options: { bullet: true, color: GRAFITE, fontSize: 15, paraSpaceAfter: 10 } },
      { text: "FAIXAS DE COR  Verde ≥ 100%   |   Azul ≥ 90%   |   Amarelo ≥ 70%   |   Vermelho < 70%.", options: { bullet: true, color: GRAFITE, fontSize: 15 } },
    ];
    s.addText(bullets, { x: 0.7, y: 1.4, w: 12, h: 4.0, fontFace: "Arial", align: "left", valign: "top" });

    const legY = 5.7;
    [["≥ 100%", "059669"], ["≥ 90%", "2563EB"], ["≥ 70%", "D97706"], ["< 70%", "DC2626"]].forEach(([lbl, col], i) => {
      const x = 0.7 + i * 2.6;
      s.addShape(pptx.ShapeType.roundRect, { x, y: legY, w: 0.45, h: 0.45, rectRadius: 0.06, fill: { color: col } });
      s.addText(lbl, { x: x + 0.55, y: legY, w: 1.9, h: 0.45, fontFace: "Arial", fontSize: 14, bold: true, color: GRAFITE, valign: "middle" });
    });
    addFooter(s, pageNo);
  }

  const stamp = new Date().toISOString().split("T")[0];
  const fileName = `mapa-estrategico-${selectedPeriod || "periodo"}-${stamp}.pptx`;
  await pptx.writeFile({ fileName });
  return fileName;
}
